import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
// 🔀 `toDateStr` was a PRIVATE copy of `toLocalISO` (three identical lines,
// declared LEGACY_DEBT group `7cd60176`); deleted 2026-09-08 for the canonical
// one. The local NAME is kept by alias because it sits alongside this file's
// `parseDate` / `addDays` / `daysDiff`, which are NOT unified — those three
// genuinely disagree between the Gantt views (see `utils/ganttDates.js`).
import { formatDate, toLocalISO as toDateStr } from '../../utils/dateFormat';
import { useVatMode } from '../../hooks/useVatMode';
import { createPortal } from 'react-dom';
import useDragScroll from '../../hooks/useDragScroll';
import useGanttScrollFrame from '../../hooks/useGanttScrollFrame';
import useGanttZoom from '../../hooks/useGanttZoom';
import GanttBarTooltip, { useGanttBarTooltip, GanttDragTooltip } from '../gantt/GanttBarTooltip';
import { buildDateHeaders } from '../../utils/ganttDates';
import GanttDateAxis, { GANTT_AXIS_Z, GANTT_DRAG_Z, GANTT_BAR_HEIGHT, GanttGrid, GanttTodayLine } from '../gantt/GanttDateAxis';
import GanttZoomControl from '../gantt/GanttZoomControl';
import StickyScrollbar from '../ui/StickyScrollbar';
import { PAGE_SCROLL_PANE_X } from '../../constants/stickyTable';
import { hireLineCost } from '../../utils/hireCost';
import { hireMoneyChangeSummary } from '../../utils/hireMoneyWarning';
import { getOrderNumber } from '../../utils/orderTitle';
import HireMoneyWarningDialog from './HireMoneyWarningDialog';
import ConfirmDialog from '../ConfirmDialog';
import { openHireClass, openHireProps, useScrollOpenHireIntoView } from './hireHighlight';
// Tooltip Extend / Off Hired take the same house pair as `HireCard.js`
// (green confirm / neutral secondary, owner ruling 2026-09-27).
import { BTN_XS } from '../../constants/buttons';

// Confirm/alt button copy per action — spec §4.1
// (docs/superpowers/specs/2026-09-02-hire-extend-dialog-design.md), "Quick
// …" for the ruling on button 3 (2026-09-02, "ok for now").
const DRAG_CONFIRM_LABEL = { extend: 'Quick extend', shorten: 'Quick shorten', change_start: 'Quick update' };
const DRAG_ALT_LABEL = {
  extend: 'Extend & view order',
  shorten: 'Shorten & view order',
  change_start: 'Update & view order',
};

// The PO-grouping sentinel and its label — deliberately NOT the string 'null'
// or '' as the map key (either would collide with a genuine PO number typed
// as that literal text), and deliberately not a value that could ever equal a
// real `po_number` (every stored one starts `PO-`, this one does not).
const NO_PO_KEY = '\u0000no-po';
const NO_PO_LABEL = 'No PO';

/**
 * Format requester name as "FirstName L." (first name + last initial)
 */
function shortName(fullName) {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

/**
 * Smart bar label that sticks to the visible left edge and progressively truncates.
 * Shows: Description · RequesterShort £price → Description · RequesterShort → Description → truncated
 */
function BarLabel({ item, barStyle, barWidth, barHeight, timelineRef }) {
  const { formatMoney } = useVatMode();
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const el = timelineRef?.current;
    if (!el) return;
    const onScroll = () => setScrollLeft(el.scrollLeft);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [timelineRef]);

  if (!barStyle || barWidth < 30) return null;

  const barLeft = barStyle.left;
  const visibleStart = scrollLeft;
  const labelLeft = Math.max(0, visibleStart - barLeft);
  const availableWidth = barWidth - labelLeft;

  if (availableWidth < 30) return null;

  const desc = item.description || 'Item';
  const requester = shortName(item.requested_by);
  const price = formatMoney(item.expected_cost || item.current_cost || 0);

  let text;
  if (availableWidth > 280) {
    text = `${desc} · ${requester} ${price}`;
  } else if (availableWidth > 180) {
    text = `${desc} · ${requester}`;
  } else if (availableWidth > 80) {
    text = desc;
  } else {
    text = desc.length > 6 ? desc.slice(0, 5) + '…' : desc;
  }

  return (
    <span
      className="absolute z-10 flex items-center text-xs font-medium text-white pointer-events-none select-none whitespace-nowrap"
      style={{
        left: labelLeft + 6,
        top: 0,
        height: barHeight,
        maxWidth: availableWidth - 12,
        overflow: 'hidden',
        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
      }}
    >
      {text}
    </span>
  );
}

/**
 * Get the colour class for a hire status
 */
function getStatusColor(status) {
  switch (status) {
    case 'pending': return { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', text: 'text-white', light: 'bg-blue-100' };
    case 'on_hire': return { bg: 'bg-green-500', hover: 'hover:bg-green-600', text: 'text-white', light: 'bg-green-100' };
    case 'overdue': return { bg: 'bg-red-500', hover: 'hover:bg-red-600', text: 'text-white', light: 'bg-red-100' };
    case 'off_hired': return { bg: 'bg-gray-400', hover: 'hover:bg-gray-500', text: 'text-white', light: 'bg-gray-100' };
    default: return { bg: 'bg-gray-400', hover: 'hover:bg-gray-500', text: 'text-white', light: 'bg-gray-100' };
  }
}

/**
 * Get rate display text. Takes the formatter as a parameter — this is a
 * module-level helper, so `formatMoney` from useVatMode() is NOT in scope
 * here. Callers must pass it in. (Caught us out on 2026-05-16 when a bulk
 * rename converted the previous formatCurrency call into formatMoney, which
 * only exists inside React component closures.)
 */
function getRateDisplay(item, fmt) {
  const type = item.hire_rate_type || 'total';
  switch (type) {
    case 'daily': return `${fmt(item.daily_rate)}/day`;
    case 'weekly': return `${fmt(item.weekly_rate)}/week`;
    case 'total':
    default: return `${fmt(item.total_hire_rate)} total`;
  }
}

/**
 * Parse YYYY-MM-DD to Date at midnight
 */
function parseDate(str) {
  if (!str) return null;
  return new Date(str + 'T00:00:00');
}

/**
 * Add days to a date
 */
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Format date as YYYY-MM-DD
 */
/**
 * Diff in days between two dates
 */
function daysDiff(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

/**
 * How many DAY COLUMNS a hire bar covers, from two parsed Dates.
 *
 * 🔑 **THE BAR MUST AGREE WITH THE PRICE, AND THAT AGREEMENT IS THE WHOLE POINT
 * OF THE 2026-08-27 RULING.** The owner asked for both halves in one sentence:
 * *"I would expect the gantt bar to show 4 days ticked AND the price to be
 * £400."* `hire_to` is the LAST HIRED DAY, so a Mon→Thu hire is FOUR columns,
 * not three — the `+ 1` is the same inclusive rule `hireDaysBetween` applies in
 * `utils/hireCost.js`, expressed in pixels.
 *
 * 🚨 **THERE ARE THREE CALL SITES AND A FIX MUST LAND ON ALL THREE.** The bar
 * geometry is drawn twice: once at rest (`getBarStyle`) and once, as a verbatim
 * copy, WHILE DRAGGING (`getDragBarWidth`, both handles). A change applied to
 * the resting copy alone gives a bar that is 4 days at rest and 3 days under
 * the user's finger — which is worse than being consistently wrong, because it
 * only misleads during the gesture the user is watching. That is exactly the
 * shape the crew-side Gantt hit with its own `getDragBar` copy. This helper
 * exists so there is one expression rather than three, and
 * `GanttView.inclusiveBar.test.js` pins all three together.
 *
 * ⚠️ It deliberately does NOT floor at 1 — every caller already wraps it in
 * `Math.max(…, 1)`, matching `billableHireDays`' floor on the money side, and
 * moving the floor in here would hide an inverted drag rather than clamp it.
 */
function barDurationDays(from, to) {
  return daysDiff(from, to) + 1;
}

/**
 * Estimate cost for a hire item given a new hire_to date.
 *
 * 🔑 **THE ARITHMETIC MOVED TO `utils/hireCost.js` ON 2026-08-11 — the rule was
 * RIGHT here and WRONG on the order pages, so this is the implementation that
 * won, not one that was replaced.** It multiplied rate by duration correctly
 * while `order_items.line_total` was being written as the bare rate; the same
 * hire line therefore read £50 on Department Orders and £1,050 on this chart.
 * One shared function now answers for both.
 *
 * 🔻 **CORRECTED 2026-08-27.** This said the day count was "byte-for-byte what
 * this function already did — exclusive diff, floored at 1". The EXCLUSIVE half
 * is no longer true on either side: `hire_to` is now the LAST HIRED DAY, so
 * `hireLineCost` counts inclusively and so does `barDurationDays` above. The
 * claim that still stands, and the one that matters, is that the money and the
 * bar are computed from the SAME rule.
 *
 * 🪤 **The DST hardening below is unchanged and must stay** — the shared
 * helper parses dates as **UTC** where the local `parseDate` above builds local
 * midnight. Measured with `TZ=Europe/London`, `Math.round` made the local parse
 * safe here, but the server's copy used `Math.ceil`, and over 2026-10-22 →
 * 2026-10-29 `ceil` + local parse returns **8** RAW days where UTC + round
 * returns **7**. Unifying on UTC + round makes both halves safe regardless of
 * which one a future reader copies.
 *
 * 🔻 **READ THOSE FIGURES AS *RAW* SPANS — corrected 2026-08-27, and this is
 * a trap the same sentence sets in four places in this repo.** Since hire spans
 * became INCLUSIVE, `hireDaysBetween('2026-10-22','2026-10-29')` legitimately
 * answers **8** — the very number named here as the BUG's answer. Nothing about
 * the hazard changed: it lives entirely in the RAW arithmetic and the inclusive
 * `+ 1` is applied after it, so the naive rewrite now returns **9**, not 8.
 */
function estimateCost(item, newHireTo, newHireFrom) {
  return hireLineCost({
    ...item,
    hire_from: newHireFrom || item.hire_from,
    hire_to: newHireTo || item.hire_to,
  });
}

/**
 * Gantt Chart View for hire items.
 * Horizontal bars spanning hire_from to hire_to on a scrollable timeline.
 * Supports drag-to-extend/shorten for admin and buyer roles.
 */
// `openItemId` (optional, 2026-09-28): the hire the Table has open — its bar
// wears `hireHighlight.js`'s mark and is scrolled into view. Absent → unchanged.
/*
 * 📜 `pageScroll` (2026-10-05, owner: *"check all gannt charts for all the
 * updates we are making"* — the Crew Gantt's page-scroll layout, applied here).
 * Off (default) = the capped `maxHeight 600` both-axes scroller with its 1:1
 * `gantt-labels` mirror, exactly as before (the phone, where `HiresPage` shows
 * the Gantt at all, keeps it). On:
 *   - no inner vertical scroller — the PAGE scrolls; a vertical drag pans the
 *     window, a horizontal one the timeline (`useDragScroll`, as the crew chart);
 *   - the date band + label header sit OUTSIDE the x-only timeline and pin to the
 *     page at `stickyTop` — the caller's MEASURED `useStickyToolbar().headerTop`;
 *   - a compact 32px header (`HIRES_PAGE_SCROLL_HEADER_HEIGHT`, passed to the
 *     shared `GanttDateAxis`, never a change to `GANTT_HEADER_HEIGHT`);
 *   - the `ui/edgeFade` mask and a `ui/StickyScrollbar` while the timeline
 *     overflows — all through `hooks/useGanttScrollFrame`, the crew chart's frame.
 * 🪤 No ancestor may be `overflow-hidden` (it would become the scroll container
 * both stickies pin to). The root is `isolate` so the band's `z-30` wins inside
 * the chart only, never over the page toolbar or app bar.
 */
export const HIRES_PAGE_SCROLL_HEADER_HEIGHT = 32;

function GanttView({ items, canManage, onDragComplete, onViewOrder, onExtend, onOffHire, onReHire, onOpenHire, openItemId = null, pageScroll = false, stickyTop = 0 }) {
  const { formatMoney } = useVatMode();
  // 'order', 'po', 'supplier', or 'date'. 'po' is NOT a fourth button — it is
  // the ORD-ID button's second state (owner, 2026-09-28: "clicking the group
  // by order ID button should cycle between order ID and PO number"). Neither
  // sub-mode is persisted to localStorage — unlike `sortDir` below, groupBy
  // never was, and this change keeps that convention rather than inventing one.
  const [groupBy, setGroupBy] = useState('order');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem('sfx_hires_gantt_sort') || 'asc');
  const timelineRef = useRef(null);
  // Zoom state, pixels per day and the keep-the-centre rule (shared).
  const { zoom, dayWidth, zoomTo, applyCentre } = useGanttZoom(timelineRef);
  const todayLineRef = useRef(null);

  // Click-and-drag panning (both axes). Bar/handle elements use data-pan-skip
  // so the existing drag-to-extend/shorten gestures keep working.
  // `pageScroll`: the timeline pans x only, and the body's vertical pan moves
  // the WINDOW (the body ref is attached only in that mode).
  const bodyRef = useRef(null);
  useDragScroll(timelineRef, pageScroll ? { axis: 'x' } : { axis: 'both', syncLabelsId: 'gantt-labels' });
  useDragScroll(bodyRef, { axis: 'y', verticalPansWindow: true });
  const {
    stickyBarRef, overflows: timelineOverflows, scrollWidth: timelineScrollWidth,
    fadeStyle: timelineFadeStyle, readFade: readTimelineFade,
    dateBandRef, dateAxisClipRef, handleBandWheel,
  } = useGanttScrollFrame(timelineRef, { fade: pageScroll });
  // The pinned date band's live bottom (`pageScroll`): a card that would sit
  // above it flips BELOW its bar (`gantt/GanttBarTooltip`).
  const getPinnedBottom = useCallback(() => (pageScroll && dateBandRef.current
    ? dateBandRef.current.getBoundingClientRect().bottom : 0), [pageScroll, dateBandRef]);

  // Drag state
  const [dragState, setDragState] = useState(null);
  const {
    tooltip, setTooltip, show: showBarTooltip, hide: hideTooltip, cancelHide: cancelHideTooltip,
  } = useGanttBarTooltip(!!dragState);
  // The drag card's anchor: the dragged item's bar, read live every frame.
  const dragItemId = dragState ? dragState.item.item_id : null;
  const getDragBarEl = useCallback(() => (dragItemId == null || !timelineRef.current ? null
    : timelineRef.current.querySelector(`[data-testid="gantt-bar-${dragItemId}"]`)), [dragItemId]);
  // `pageScroll` makes the root `isolate`, which would trap an in-tree `fixed`
  // child under the app bar — so the drag card and the money dialog leave the
  // chart for `document.body` in that mode (the z-index contract's escape tier).
  const outOfChart = (node) => (pageScroll ? createPortal(node, document.body) : node);
  // { item, handle: 'left'|'right', startX, originalHireTo, originalHireFrom, currentDateStr, barLeft, originalBarWidth }
  const [confirmDialog, setConfirmDialog] = useState(null);
  // `{ item, newDateStr }` — an end-handle drag that shortened the hire to a
  // date already passed (see `handleDragEnd`); shows the "record it as
  // off-hired" dialog instead of the money dialog.
  const [pastShorten, setPastShorten] = useState(null);
  // { item, newDateStr, estimatedCost, action }
  const [confirmLoading, setConfirmLoading] = useState(false);


  // Compute timeline range from data
  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    if (!items || items.length === 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return { timelineStart: today, timelineEnd: addDays(today, 30), totalDays: 30 };
    }

    let minDate = null;
    let maxDate = null;

    items.forEach(item => {
      const from = parseDate(item.hire_from);
      const to = parseDate(item.hire_to);
      if (from && (!minDate || from < minDate)) minDate = from;
      if (to && (!maxDate || to > maxDate)) maxDate = to;
    });

    // Add padding: 7 days before and after
    const start = addDays(minDate || new Date(), -7);
    // Snap to Monday
    const dow = start.getDay();
    if (dow !== 1) {
      start.setDate(start.getDate() - ((dow + 6) % 7));
    }
    const end = addDays(maxDate || new Date(), 14);
    const total = daysDiff(start, end);

    return { timelineStart: start, timelineEnd: end, totalDays: Math.max(total, 30) };
  }, [items]);

  // Today position
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const todayOffset = daysDiff(timelineStart, today);

  // Scroll to today on mount / new items; a zoom change keeps the centre.
  useEffect(() => {
    if (applyCentre()) return;
    if (todayLineRef.current && timelineRef.current) {
      const container = timelineRef.current;
      const todayPos = todayOffset * dayWidth;
      // Center today in the visible area
      const scrollTo = todayPos - container.clientWidth / 3;
      container.scrollLeft = Math.max(0, scrollTo);
    }
  }, [todayOffset, dayWidth, items, applyCentre]);

  // …then, on mount, the hire the Table has open wins over "today": declared
  // AFTER the today scroll so it runs after it (effects run in order).
  useScrollOpenHireIntoView(openItemId, timelineRef);

  // Group items
  const groups = useMemo(() => {
    if (groupBy === 'date') {
      // Flat list sorted by start or end date
      const dateField = sortDir === 'end' ? 'hire_to' : 'hire_from';
      const sorted = [...items].sort((a, b) => (a[dateField] || '').localeCompare(b[dateField] || ''));
      return [{ label: 'All Items', items: sorted }];
    }

    const map = {};
    items.forEach(item => {
      if (groupBy === 'order') {
        // 🔀 The order NUMBER as the group heading (2026-08-12). Hire rows
        // carry `order_number` (GET /api/hires selects `o.order_number`).
        // 🔀 2026-09-28: the PO joins it when the order has one — owner,
        // "add in the PO number next to the order ID". `item.po_number` is
        // ALREADY normalised WITH its `PO-` prefix by every writer
        // (server/helpers/poNumber.js) — never re-prepend one here, that is
        // the `PO:PO-147` doubling class `po-doubled-prefix-manifest.test.js`
        // ratchets. Grouping KEY stays the bare order number (so every item
        // on the order lands in one group regardless of whether its row
        // happened to carry the PO), only the DISPLAYED label grows the PO.
        const key = getOrderNumber(item) || 'Unknown order';
        if (!map[key]) {
          const label = item.po_number ? `${key} · ${item.po_number}` : key;
          map[key] = { label, sortKey: key, items: [] };
        }
        map[key].items.push(item);
      } else if (groupBy === 'po') {
        // Grouping BY PO — owner ruling: an order with no PO goes in a single
        // "No PO" group, at the end (not sorted alphabetically with the rest).
        const key = item.po_number || NO_PO_KEY;
        if (!map[key]) {
          map[key] = { label: key === NO_PO_KEY ? NO_PO_LABEL : key, sortKey: key, items: [] };
        }
        map[key].items.push(item);
      } else {
        const key = item.supplier_name || 'No Supplier';
        if (!map[key]) {
          map[key] = { label: key, sortKey: key, items: [] };
        }
        map[key].items.push(item);
      }
    });

    // Sort groups by label — except in PO mode, where "No PO" is pinned LAST
    // regardless of sort direction (owner ruling: "at the end").
    const sorted = Object.values(map).sort((a, b) => {
      if (groupBy === 'po') {
        if (a.sortKey === NO_PO_KEY) return 1;
        if (b.sortKey === NO_PO_KEY) return -1;
      }
      const cmp = a.label.localeCompare(b.label);
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [items, groupBy, sortDir]);

  // Build date headers based on zoom
  // THE shared date-axis cells (`utils/ganttDates`); this chart built its own
  // identical copy until 2026-10-13.
  const dateHeaders = useMemo(() => buildDateHeaders(zoom, timelineStart, totalDays), [zoom, timelineStart, totalDays]);

  const totalWidth = totalDays * dayWidth;
  const rowHeight = 42;
  const headerHeight = pageScroll ? HIRES_PAGE_SCROLL_HEADER_HEIGHT : 48;
  const labelWidth = 220;
  const labelHeaderText = groupBy === 'date' ? 'Item' : groupBy === 'order' ? 'ORD-ID / Item' : groupBy === 'po' ? 'PO # / Item' : 'Supplier / Item';

  // Compute bar position for an item
  function getBarStyle(item) {
    const from = parseDate(item.hire_from);
    const to = parseDate(item.hire_to);
    if (!from || !to) return null;

    const startDay = daysDiff(timelineStart, from);
    const duration = Math.max(barDurationDays(from, to), 1);

    return {
      left: startDay * dayWidth,
      width: Math.max(duration * dayWidth, 4),
    };
  }

  // Hover card (non-drag) with a delayed hide so the pointer can reach its
  // buttons — `gantt/GanttBarTooltip`, shared with the crew and cross-team Gantts.
  const showTooltip = (e, item) => showBarTooltip(e, { item });

  // ---- DRAG LOGIC ----

  // Convert pixel delta to day delta
  const pxToDays = useCallback((px) => {
    return Math.round(px / dayWidth);
  }, [dayWidth]);

  // Start drag from edge of a bar
  const handleDragStart = useCallback((e, item, handle = 'right') => {
    if (!canManage) return;
    if (item.hire_status === 'off_hired') return; // Can't drag off-hired items
    if (handle === 'left' && item.hire_status !== 'pending') return; // Left drag only for pending

    e.preventDefault();
    e.stopPropagation();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const bar = getBarStyle(item);

    setDragState({
      item,
      handle,
      startX: clientX,
      originalHireTo: item.hire_to,
      originalHireFrom: item.hire_from,
      currentDateStr: handle === 'left' ? item.hire_from : item.hire_to,
      barLeft: bar ? bar.left : 0,
      originalBarWidth: bar ? bar.width : 0,
    });
    setTooltip(null); // Clear normal tooltip
  }, [canManage, dayWidth, timelineStart, setTooltip]);

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!dragState) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragState.startX;
    const deltaDays = pxToDays(deltaX);

    if (dragState.handle === 'left') {
      const originalFrom = parseDate(dragState.originalHireFrom);
      const newFrom = addDays(originalFrom, deltaDays);
      const to = parseDate(dragState.item.hire_to);
      // Don't allow dragging past hire_to - 1 day
      if (to && newFrom >= to) return;
      setDragState(prev => ({ ...prev, currentDateStr: toDateStr(newFrom) }));
    } else {
      const originalTo = parseDate(dragState.originalHireTo);
      const newTo = addDays(originalTo, deltaDays);
      const from = parseDate(dragState.item.hire_from);

      // Don't allow dragging before hire_from + 1 day
      if (from && newTo <= from) return;

      const newDateStr = toDateStr(newTo);

      setDragState(prev => ({
        ...prev,
        currentDateStr: newDateStr,
      }));
    }
  }, [dragState, pxToDays]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (!dragState) return;

    const newDateStr = dragState.currentDateStr;

    if (dragState.handle === 'left') {
      const originalDateStr = dragState.originalHireFrom;
      if (newDateStr === originalDateStr) { setDragState(null); return; }
      const action = 'change_start';
      const cost = estimateCost(dragState.item, dragState.item.hire_to, newDateStr);
      setConfirmDialog({ item: dragState.item, newDateStr, estimatedCost: cost, action });
    } else {
      const originalDateStr = dragState.originalHireTo;

      // If no change, just cancel
      if (newDateStr === originalDateStr) {
        setDragState(null);
        return;
      }

      const isExtend = newDateStr > originalDateStr;
      const action = isExtend ? 'extend' : 'shorten';

      // ⚖️ Owner ruling 2026-10-05, "Point them to Off hire": a shorten to a
      // date that has already PASSED (LOCAL today — `toLocalISO`, never
      // `toISOString`) is not sent. `PATCH /shorten` stays future-only on the
      // server (its "must be in the future" 400 is the backstop) and
      // shortening reprices, whereas an item that went back on a past date is
      // an OFF-HIRE, whose own dialog carries the price choice and never
      // auto-reprices. So: no money dialog, no PATCH — offer Off hire instead.
      // Dropping the drag state below is what "Change date" reverts to.
      if (action === 'shorten' && newDateStr < toDateStr(new Date())) {
        setPastShorten({ item: dragState.item, newDateStr });
        setDragState(null);
        return;
      }

      const cost = estimateCost(dragState.item, newDateStr);

      setConfirmDialog({
        item: dragState.item,
        newDateStr,
        estimatedCost: cost,
        action,
      });
    }

    setDragState(null);
  }, [dragState]);

  // Attach global mouse/touch move and end listeners during drag
  useEffect(() => {
    if (!dragState) return;

    const onMove = (e) => handleDragMove(e);
    const onEnd = () => handleDragEnd();

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);

    // Add a body class to prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragState, handleDragMove, handleDragEnd]);

  // Confirm drag action. `options` carries the navigate-intent variant —
  // `{ viewOrder: true }` from the alt ("… & view order") button, `undefined`
  // from the plain ("Quick …") confirm — forwarded verbatim as a 4th argument
  // to `onDragComplete`, whose page-level implementation decides what to do
  // with it (navigate on success; the intent must never survive a failure or
  // a later, different action — the page owns that, not this component).
  //
  // 🚨 NAVIGATE-ONLY-ON-SUCCESS is enforced by the CALLER, not here: this
  // function's own contract — close the dialog ONLY inside the `try`, after
  // `onDragComplete` resolves — is unchanged by adding `options`, so a
  // rejected/thrown `onDragComplete` (a non-ok PATCH) leaves `confirmDialog`
  // set and the money dialog open, exactly as before. The page's
  // `handleDragComplete` must not call `handleViewOrder` until AFTER this
  // resolves without throwing.
  const runConfirm = useCallback(async (options) => {
    if (!confirmDialog || !onDragComplete) return;

    setConfirmLoading(true);
    try {
      await onDragComplete(confirmDialog.item, confirmDialog.newDateStr, confirmDialog.action, options);
      setConfirmDialog(null);
    } catch (err) {
      // Keep dialog open on error, parent handles alert
    } finally {
      setConfirmLoading(false);
    }
  }, [confirmDialog, onDragComplete]);

  const handleConfirm = useCallback(() => runConfirm(undefined), [runConfirm]);
  const handleConfirmAndViewOrder = useCallback(() => runConfirm({ viewOrder: true }), [runConfirm]);

  // Cancel confirmation
  const handleCancelConfirm = useCallback(() => {
    if (!confirmLoading) {
      setConfirmDialog(null);
    }
  }, [confirmLoading]);

  // Get the visual bar width for an item during drag
  function getDragBarWidth(item) {
    if (dragState && dragState.item.item_id === item.item_id) {
      if (dragState.handle === 'left') {
        const newFrom = parseDate(dragState.currentDateStr);
        const to = parseDate(item.hire_to);
        if (newFrom && to) {
          const duration = Math.max(barDurationDays(newFrom, to), 1);
          return Math.max(duration * dayWidth, 4);
        }
      } else {
        const from = parseDate(item.hire_from);
        const newTo = parseDate(dragState.currentDateStr);
        if (from && newTo) {
          const duration = Math.max(barDurationDays(from, newTo), 1);
          return Math.max(duration * dayWidth, 4);
        }
      }
    }
    return null; // use normal width
  }

  // Get the visual bar left position for an item during left-drag
  function getDragBarLeft(item) {
    if (dragState && dragState.handle === 'left' && dragState.item.item_id === item.item_id) {
      const newFrom = parseDate(dragState.currentDateStr);
      if (newFrom) {
        return daysDiff(timelineStart, newFrom) * dayWidth;
      }
    }
    return null;
  }

  // Count total rows
  let totalRows = 0;
  groups.forEach(g => {
    if (groupBy !== 'date') totalRows += 1; // group header (skip for date - flat list)
    totalRows += g.items.length;
  });

  // Drag tooltip position
  const dragTooltipContent = dragState ? (() => {
    const item = dragState.item;
    const newDateStr = dragState.currentDateStr;

    if (dragState.handle === 'left') {
      const noChange = newDateStr === dragState.originalHireFrom;
      const isEarlier = !noChange && newDateStr < dragState.originalHireFrom;
      const cost = estimateCost(item, item.hire_to, newDateStr);
      return {
        newDate: formatDate(newDateStr),
        originalDate: formatDate(dragState.originalHireFrom),
        cost: formatMoney(cost),
        action: noChange ? 'NO CHANGE' : isEarlier ? 'EARLIER' : 'LATER',
        isExtend: isEarlier,  // blue border for extending (earlier start)
        isShrink: !noChange && !isEarlier,  // amber for shrinking (later start)
        noChange,
        isLeftDrag: true,
      };
    }

    // Right-drag logic
    const noChange = newDateStr === dragState.originalHireTo;
    const isExtend = !noChange && newDateStr > dragState.originalHireTo;
    const isShrink = !noChange && !isExtend;
    const cost = estimateCost(item, newDateStr);
    return {
      newDate: formatDate(newDateStr),
      originalDate: formatDate(dragState.originalHireTo),
      cost: formatMoney(cost),
      action: noChange ? 'NO CHANGE' : isExtend ? 'EXTEND' : 'SHORTEN',
      isExtend,
      isShrink,
      noChange,
      isLeftDrag: false,
    };
  })() : null;

  // The label column's rows — one list, rendered by both column layouts below.
  const labelRows = groups.map(group => (
    <React.Fragment key={group.label}>
      {/* Group header row */}
      {groupBy !== 'date' && (
        <div
          className="flex items-center px-3 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 font-semibold text-xs text-gray-700 dark:text-gray-200 truncate"
          style={{ height: rowHeight }}
          title={group.label}
        >
          {group.label}
          <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">({group.items.length})</span>
        </div>
      )}
      {/* Item rows */}
      {group.items.map(item => (
        <div
          key={item.item_id}
          className={`flex items-center px-3 border-b border-gray-100 dark:border-gray-700 text-xs truncate ${
            openItemId != null && item.item_id === openItemId
              ? 'font-semibold text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30'
              : 'text-gray-600 dark:text-gray-300'
          }`}
          style={{ height: rowHeight }}
          title={item.description || 'Unnamed'}
          data-testid={`gantt-label-${item.item_id}`}
        >
          {item.description || 'Unnamed item'}
        </div>
      ))}
    </React.Fragment>
  ));

  return (
    <div className={`${pageScroll ? 'isolate ' : ''}bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700`} data-testid="gantt-view">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-lg" data-section-title>
        {/* Group toggle */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Group by</span>
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              // Cycles ORD-ID → PO # → ORD-ID (owner, 2026-09-28). Arriving
              // from Supplier or Date always lands on ORD-ID first — a fresh
              // "group by order" click should not silently resume PO mode.
              onClick={() => setGroupBy(prev => (prev === 'order' ? 'po' : 'order'))}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                groupBy === 'order' || groupBy === 'po'
                  ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
              title={groupBy === 'po' ? 'Grouped by PO number — click to group by order ID' : 'Grouped by order ID — click to group by PO number'}
              data-testid="gantt-group-order"
              data-groupby-mode={groupBy === 'po' ? 'po' : 'order'}
            >
              {groupBy === 'po' ? 'PO #' : 'ORD-ID'}
            </button>
            <button
              onClick={() => setGroupBy('supplier')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                groupBy === 'supplier'
                  ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
              data-testid="gantt-group-supplier"
            >
              Supplier
            </button>
            <button
              onClick={() => setGroupBy('date')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                groupBy === 'date'
                  ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
              data-testid="gantt-group-date"
            >
              Date
            </button>
          </div>
          <button
            onClick={() => {
              if (groupBy === 'date') {
                const next = sortDir === 'end' ? 'start' : 'end';
                setSortDir(next);
                localStorage.setItem('sfx_hires_gantt_sort', next);
              } else {
                const next = sortDir === 'asc' ? 'desc' : 'asc';
                setSortDir(next);
                localStorage.setItem('sfx_hires_gantt_sort', next);
              }
            }}
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            data-chevron
            title={groupBy === 'date' ? (sortDir === 'end' ? 'Sort by end date' : 'Sort by start date') : (sortDir === 'asc' ? 'A → Z' : 'Z → A')}
            data-testid="gantt-sort-dir"
          >
            <svg className={`w-4 h-4 transition-transform ${sortDir === 'desc' || sortDir === 'end' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <GanttZoomControl zoom={zoom} onZoom={zoomTo} testIdPrefix="gantt-zoom" />
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 text-xs text-gray-700 dark:text-gray-200">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block"></span> Pending</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block"></span> On Hire</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block"></span> Overdue</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-400 inline-block"></span> Off-Hired</span>
        </div>
      </div>

      {/* Gantt body — `pageScroll`: a `relative` box holding the page-pinned
          date band, then the label column + x-only timeline row. Otherwise the
          capped both-axes scroller it always was. */}
      <div
        ref={pageScroll ? bodyRef : undefined}
        data-testid="gantt-body"
        className={pageScroll ? 'relative' : 'flex overflow-hidden'}
        style={pageScroll ? undefined : { maxHeight: '600px' }}
      >
        {pageScroll && (
          /* 📌 The date band — OUTSIDE the timeline (a sticky child of an
             x-only scroller is inert), opaque, `GANTT_AXIS_Z`, pinned under the
             page toolbar at the measured `stickyTop`. Its clip follows the
             timeline; a drag or `deltaX` wheel on it pans the timeline. */
          <div
            ref={dateBandRef}
            data-testid="gantt-date-band"
            className={`sticky top-0 ${GANTT_AXIS_Z} flex cursor-grab bg-white dark:bg-gray-800`}
            style={{ height: headerHeight, top: stickyTop }}
            onWheel={handleBandWheel}
          >
            <div
              className="flex-shrink-0 border-r border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 flex items-center"
              style={{ width: labelWidth }}
            >
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {labelHeaderText}
              </span>
            </div>
            <div ref={dateAxisClipRef} data-testid="gantt-date-band-clip" className="flex-1 min-w-0 overflow-hidden" style={timelineFadeStyle}>
              <GanttDateAxis
                testId="gantt-date-axis"
                className="relative"
                style={{ width: totalWidth, minWidth: '100%' }}
                dateHeaders={dateHeaders}
                dayWidth={dayWidth}
                height={headerHeight}
              />
            </div>
          </div>
        )}
        <div className={pageScroll ? 'flex' : 'contents'}>
        {/* Fixed left label column. `pageScroll`: a plain column (the band's
            corner carries the heading; the page scrolls both columns).
            Otherwise the capped column whose labels div is MIRRORED 1:1 from the
            timeline (`gantt-label-scroll-sync.test.js` reads this markup). */}
        {pageScroll ? (
          <div className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900" style={{ width: labelWidth }}>
            {labelRows}
          </div>
        ) : (
        <div className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col" style={{ width: labelWidth }}>
          {/* Header spacer */}
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-3 flex items-center" style={{ height: headerHeight }}>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {labelHeaderText}
            </span>
          </div>
          {/* Row labels */}
          <div className="flex-1 overflow-hidden" id="gantt-labels">
            {labelRows}
          </div>
        </div>
        )}

        {/* Scrollable timeline area — panning handled by useDragScroll above */}
        <div
          className={pageScroll ? `flex-1 ${PAGE_SCROLL_PANE_X} cursor-grab` : 'flex-1 overflow-auto cursor-grab'}
          ref={timelineRef}
          data-testid="gantt-timeline-scrollport"
          style={timelineFadeStyle}
          onScroll={(e) => {
            if (pageScroll) { readTimelineFade(); return; }
            // Sync label scroll with timeline vertical scroll
            const labels = document.getElementById('gantt-labels');
            if (labels) labels.scrollTop = e.target.scrollTop;
          }}
        >
          <div style={{ width: totalWidth, minWidth: '100%' }}>
            {/* Date headers (`pageScroll`: in the band above instead) */}
            {!pageScroll && (
            <div className="relative border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" style={{ height: headerHeight }}>
              {dateHeaders.map((h, i) => (
                <div
                  key={i}
                  className={`absolute top-0 flex flex-col items-start justify-center px-1 border-r border-gray-100 dark:border-gray-700 text-xs ${
                    h.isWeekend ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                  style={{
                    left: h.offset * dayWidth,
                    width: h.width * dayWidth,
                    height: headerHeight,
                  }}
                >
                  <span className="text-gray-600 dark:text-gray-300 font-medium truncate w-full">{h.label}</span>
                  {h.sublabel && <span className="text-gray-400 dark:text-gray-500 text-[10px]">{h.sublabel}</span>}
                </div>
              ))}
            </div>
            )}

            {/* Chart rows */}
            <div className="relative">
              {/* Background grid lines (every header) */}
              <GanttGrid dateHeaders={dateHeaders} dayWidth={dayWidth} height={totalRows * rowHeight} />
              <GanttTodayLine todayOffset={todayOffset} totalDays={totalDays} dayWidth={dayWidth} height={totalRows * rowHeight} lineRef={todayLineRef} />

              {/* Rows */}
              {(() => {
                let rowIndex = 0;
                return groups.map(group => (
                  <React.Fragment key={group.label}>
                    {/* Group header row */}
                    {groupBy !== 'date' && (
                      <div
                        className="relative bg-gray-100/60 dark:bg-gray-700/40 border-b border-gray-200 dark:border-gray-600"
                        style={{ height: rowHeight, top: 0 }}
                      >
                        {(() => { rowIndex++; return null; })()}
                      </div>
                    )}
                    {/* Item bars */}
                    {group.items.map(item => {
                      const bar = getBarStyle(item);
                      const colors = getStatusColor(item.hire_status);
                      const isDragging = dragState && dragState.item.item_id === item.item_id;
                      const dragBarWidth = getDragBarWidth(item);
                      const canDrag = canManage && item.hire_status !== 'off_hired';
                      const isRightDrag = isDragging && dragState?.handle === 'right';
                      const isLeftDrag = isDragging && dragState?.handle === 'left';
                      rowIndex++;

                      return (
                        <div
                          key={item.item_id}
                          className="relative border-b border-gray-100 dark:border-gray-700"
                          style={{ height: rowHeight }}
                        >
                          {bar && (() => {
                            const totalWidth = isDragging && dragBarWidth ? dragBarWidth : bar.width;
                            const isExtending = (isRightDrag && dragBarWidth && dragBarWidth > bar.width) ||
                                                (isLeftDrag && dragBarWidth && dragBarWidth > bar.width);
                            const isShrinking = isDragging && dragBarWidth && dragBarWidth < bar.width;
                            const barLeftPos = (isDragging && dragState?.handle === 'left' ? getDragBarLeft(item) : null) ?? bar.left;
                            return (
                            <div
                              data-testid={`gantt-bar-${item.item_id}`}
                              className="absolute top-2.5 overflow-visible"
                              style={{
                                left: barLeftPos,
                                width: totalWidth,
                                height: GANTT_BAR_HEIGHT,
                                // `GANTT_DRAG_Z` (27): strictly below the page-scroll band's
                                // `z-30` axis (was a 30 tie, which the later bar won).
                                zIndex: isDragging ? GANTT_DRAG_Z : 10,
                              }}
                              data-testid={`gantt-bar-${item.item_id}`}
                              data-pan-skip
                            >
                              {/* Extended portion (ghost/transparent) — only during extend drag */}
                              {isExtending && (
                                <div
                                  className={`absolute top-0 ${colors.bg} opacity-30`}
                                  style={{
                                    left: isLeftDrag ? 0 : bar.width,
                                    width: totalWidth - bar.width,
                                    height: '100%',
                                    borderRight: isLeftDrag ? '2px dashed rgba(255,255,255,0.5)' : undefined,
                                    borderLeft: !isLeftDrag ? '2px dashed rgba(255,255,255,0.5)' : undefined,
                                    borderRadius: isLeftDrag ? '0.375rem 0 0 0.375rem' : '0 0.375rem 0.375rem 0',
                                  }}
                                />
                              )}
                              {/* Original portion (solid) */}
                              <div
                                className={`absolute top-0 rounded-md ${colors.bg} ${isDragging ? '' : colors.hover} ${colors.text} transition-none shadow-sm hover:shadow-md flex items-center px-2 cursor-pointer ${openHireClass(item.item_id, openItemId)}`}
                                {...openHireProps(item.item_id, openItemId)}
                                style={{
                                  left: isExtending && isLeftDrag ? totalWidth - bar.width : 0,
                                  width: isDragging ? Math.min(bar.width, totalWidth) : totalWidth,
                                  height: '100%',
                                  borderRadius: isExtending ? (isLeftDrag ? '0 0.375rem 0.375rem 0' : '0.375rem 0 0 0.375rem') : undefined,
                                }}
                                // A plain click on the BAR opens the hire (`onOpenHire`: the hires
                                // Table, expanded); AdminHires passes none and keeps its order link.
                                // A drag starts only on the EDGE handles (siblings of this element,
                                // `stopPropagation` on mousedown), so a drag never reaches here.
                                onClick={() => { if (dragState) return; if (onOpenHire) onOpenHire(item); else if (onViewOrder) onViewOrder(item.order_id); }}
                                onMouseEnter={(e) => showTooltip(e, item)}
                                onMouseLeave={hideTooltip}
                              />
                              {/* Shrink indicator — dashed border during shrink drag */}
                              {isShrinking && (
                                <div
                                  className="absolute top-0 border-2 border-dashed border-amber-400 opacity-40"
                                  style={{
                                    left: isLeftDrag ? totalWidth : 0,
                                    width: bar.width - totalWidth,
                                    height: '100%',
                                    borderRadius: isLeftDrag ? '0.375rem 0 0 0.375rem' : '0 0.375rem 0.375rem 0',
                                  }}
                                />
                              )}
                              {/* Bar label spans full width */}
                              <BarLabel
                                item={item}
                                barStyle={bar}
                                barWidth={totalWidth}
                                barHeight={GANTT_BAR_HEIGHT}
                                timelineRef={timelineRef}
                              />

                              {/* Drag handle on right edge */}
                              {canDrag && (
                                <div
                                  className="absolute top-0 right-0 w-2 h-full cursor-col-resize group/handle flex items-center justify-center"
                                  style={{ marginRight: -1 }}
                                  onMouseDown={(e) => handleDragStart(e, item, 'right')}
                                  onTouchStart={(e) => handleDragStart(e, item, 'right')}
                                  data-testid={`gantt-drag-handle-${item.item_id}`}
                                >
                                  <div className="w-1 h-3 rounded-sm bg-white/50 group-hover/handle:bg-white/90 transition-colors" />
                                </div>
                              )}
                              {/* Left drag handle - only for pending items */}
                              {canDrag && item.hire_status === 'pending' && (
                                <div
                                  className="absolute top-0 left-0 w-2 h-full cursor-col-resize group/lhandle flex items-center justify-center"
                                  style={{ marginLeft: -1 }}
                                  onMouseDown={(e) => handleDragStart(e, item, 'left')}
                                  onTouchStart={(e) => handleDragStart(e, item, 'left')}
                                  data-testid={`gantt-drag-left-handle-${item.item_id}`}
                                >
                                  <div className="w-1 h-3 rounded-sm bg-white/50 group-hover/lhandle:bg-white/90 transition-colors" />
                                </div>
                              )}
                            </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ));
              })()}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* 📜 `pageScroll`'s sticky horizontal bar — only while the timeline
          overflows; `marginLeft: labelWidth` makes its range the timeline's. */}
      {pageScroll && timelineOverflows ? (
        <StickyScrollbar
          ref={stickyBarRef}
          contentStyle={{ width: timelineScrollWidth }}
          testId="gantt-scrollbar"
          style={{ marginLeft: labelWidth }}
        />
      ) : null}

      {/* Normal tooltip (non-drag) */}
      {tooltip && !dragState && (
        <GanttBarTooltip
          tooltip={tooltip}
          getMinTop={getPinnedBottom}
          testId="gantt-tooltip"
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={() => { setTooltip(null); }}
        >
          <div className="font-semibold mb-1">{tooltip.item.description || 'Unnamed item'}</div>
          <div className="text-gray-100 space-y-0.5">
            <div>Supplier: {tooltip.item.supplier_name || '—'}</div>
            <div>Dates: {formatDate(tooltip.item.hire_from)} → {formatDate(tooltip.item.hire_to)}</div>
            <div>Rate: {getRateDisplay(tooltip.item, formatMoney)}</div>
            <div>Expected: {formatMoney(tooltip.item.expected_cost)} | Current: {formatMoney(tooltip.item.current_cost)}</div>
            <div>Status: {tooltip.item.hire_status?.replace('_', ' ')}</div>
            <div>Order: {getOrderNumber(tooltip.item)} • {tooltip.item.requested_by}</div>
          </div>
          <div className="mt-2 flex flex-col gap-1">
            {onViewOrder && (
              <button
                onClick={() => { setTooltip(null); onViewOrder(tooltip.item.order_id); }}
                className="w-full text-xs px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded font-medium transition-colors inline-flex items-center justify-center gap-1"
                data-testid={`gantt-view-order-btn-${tooltip.item.item_id}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Order
              </button>
            )}
            {canManage && onExtend && ['on_hire', 'overdue', 'pending'].includes(tooltip.item.hire_status) && (
              <button
                onClick={() => { setTooltip(null); onExtend(tooltip.item); }}
                className={`${BTN_XS.confirm} w-full justify-center`}
                data-testid={`gantt-extend-btn-${tooltip.item.item_id}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Extend Hire
              </button>
            )}
            {canManage && onOffHire && ['on_hire', 'overdue'].includes(tooltip.item.hire_status) && (
              <button
                onClick={() => { setTooltip(null); onOffHire(tooltip.item); }}
                className={`${BTN_XS.secondary} w-full justify-center`}
                data-testid={`gantt-offhire-btn-${tooltip.item.item_id}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Off Hired
              </button>
            )}
            {canManage && onReHire && tooltip.item.hire_status === 'off_hired' && (
              <button
                onClick={() => { setTooltip(null); onReHire(tooltip.item); }}
                className="w-full text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500/70 dark:hover:bg-blue-500/85 text-white rounded font-medium transition-colors inline-flex items-center justify-center gap-1"
                data-testid={`gantt-rehire-btn-${tooltip.item.item_id}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Bring Back On Hire
              </button>
            )}
          </div>
        </GanttBarTooltip>
      )}

      {/* Drag card — anchored to the DRAGGED BAR (owner 2026-10-05: *"the tool
          tips when dragging are appearing in what seem to be random places"*).
          It was pinned to the VIEWPORT CENTRE (`left/top: 50%`) whatever the
          bar's position. `gantt/GanttDragTooltip` tracks the bar's live rect:
          above it, below when the pinned band is in the way, pointer-events-none. */}
      {dragState && dragTooltipContent && (
        <GanttDragTooltip getAnchor={getDragBarEl} getMinTop={getPinnedBottom}>
          <div className={`bg-gray-900 text-white text-xs rounded-lg shadow-xl px-4 py-3 border-2 ${
            dragTooltipContent.isExtend ? 'border-blue-400' : dragTooltipContent.isShrink ? 'border-amber-400' : 'border-gray-500'
          }`} data-testid="gantt-drag-tooltip">
            <div className="font-semibold mb-1.5 flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                dragTooltipContent.isExtend ? 'bg-blue-500' : dragTooltipContent.isShrink ? 'bg-amber-500' : 'bg-gray-500'
              }`}>
                {dragTooltipContent.action}
              </span>
              {dragState.item.description || 'Item'}
            </div>
            <div className="text-gray-300 space-y-0.5">
              <div>{dragTooltipContent.isLeftDrag ? 'Original start:' : 'Original end:'} {dragTooltipContent.originalDate}</div>
              {!dragTooltipContent.noChange && (
                <div className="text-white font-medium">{dragTooltipContent.isLeftDrag ? 'New start:' : 'New end:'} {dragTooltipContent.newDate}</div>
              )}
              <div className="text-white font-medium">Est. cost: {dragTooltipContent.cost}</div>
            </div>
          </div>
        </GanttDragTooltip>
      )}

      {/* Confirmation dialog — the money warning gates every drag-driven
          extend/shorten/change-start PATCH. Was a hand-rolled <div> with no
          focus trap, no Escape handling and no dialog role; now built on the
          shared ConfirmDialog via HireMoneyWarningDialog. See
          utils/hireMoneyWarning.js for the wording rules and rows/sentence.
          Rows are the current/new price PAIR, replacing "Estimated cost" —
          "one figure named two ways across surfaces" is the cross-surface
          disagreement class this repo records (spec §3.1). */}
      {confirmDialog && (() => {
        const action = confirmDialog.action;
        const confirmLabel = confirmLoading ? 'Processing…' : DRAG_CONFIRM_LABEL[action];
        const summary = hireMoneyChangeSummary(action, confirmDialog.item, confirmDialog.newDateStr, formatMoney);
        return outOfChart((
          <HireMoneyWarningDialog
            open
            action={action}
            item={confirmDialog.item}
            busy={confirmLoading}
            confirmLabel={confirmLabel}
            altLabel={DRAG_ALT_LABEL[action]}
            altVariant="confirm"
            onAlt={handleConfirmAndViewOrder}
            onConfirm={handleConfirm}
            onCancel={handleCancelConfirm}
          >
            <div className="text-sm space-y-1.5" data-testid="gantt-confirm-details">
              <div className="font-medium text-gray-800 dark:text-gray-100 truncate">
                {confirmDialog.item.description}
              </div>
              {summary && summary.rows.map((row) => (
                <div className="flex justify-between" key={row.label}>
                  <span className="text-gray-500 dark:text-gray-400">{row.label}</span>
                  <span className={`font-medium ${row.highlight ? 'text-blue-700 dark:text-blue-400' : 'dark:text-gray-200'}`}>
                    {row.value}
                  </span>
                </div>
              ))}
              {summary && summary.sentence && (
                <p
                  className={`rounded-lg border p-2 ${
                    summary.offer
                      ? 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                      : 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600'
                  }`}
                  data-testid="gantt-confirm-sentence"
                >
                  {summary.sentence}
                </p>
              )}
              {/* The explainer line — spec §4.2. Always last, above the
                  buttons: "commits are not ambiguous" for either exit. */}
              <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="gantt-confirm-explainer">
                Both buttons set the new date and update the price. Cancel makes no change.
              </p>
            </div>
          </HireMoneyWarningDialog>
        ));
      })()}

      {/* Past-date shorten → Off hire (owner ruling 2026-10-05). "Off hire on
          <date>" opens the page's EXISTING off-hire dialog pre-filled with the
          dragged date (`onOffHire(item, date)`), where the price choice
          happens; "Change date" (Cancel, also Escape/overlay) closes it, and
          the bar is already back at its stored end date. Never a PATCH here. */}
      {pastShorten && outOfChart((
        <ConfirmDialog
          open
          title="End date has passed"
          message={`${formatDate(pastShorten.newDateStr)} has already passed. If the item has gone back, record it as off-hired:`}
          confirmLabel={`Off hire on ${formatDate(pastShorten.newDateStr)}`}
          confirmVariant="confirm"
          cancelLabel="Change date"
          onConfirm={() => {
            const { item, newDateStr } = pastShorten;
            setPastShorten(null);
            if (onOffHire) onOffHire(item, newDateStr);
          }}
          onCancel={() => setPastShorten(null)}
        />
      ))}
    </div>
  );
}

export default GanttView;
